import { newRelic } from '~/utils/newRelic/newRelic';

const ORDERS_FETCH = 'orders-fetch';
const ORDERS_FETCH_START = 'orders-fetch-start';

const PURCHASEABLE_ORDERS = 'purchaseable-orders';
const PURCHASEABLE_ORDERS_START = 'purchaseable-orders-start';

const BATCH_ENTRY_POLL = 'batch-entry-poll';
const BATCH_ENTRY_POLL_START = 'batch-entry-poll-start';

const SHIPMENT_POLL = 'shipment-poll';
const SHIPMENT_POLL_START = 'shipment-poll-start';

const isMeasuringPerformance = () => {
  try {
    const purchaseableOrdersStartMarks = performance.getEntriesByName(PURCHASEABLE_ORDERS_START);
    return purchaseableOrdersStartMarks.length > 0;
  } catch (error) {
    return false;
  }
};

const calculateDurationPerOrder = (duration: number, orderCount: number) => {
  if (orderCount === 0) return null;
  return duration / orderCount;
};

export const fetchOrdersPerformanceStart = () => {
  try {
    performance.clearMarks();
    performance.mark(ORDERS_FETCH_START);
    performance.mark(PURCHASEABLE_ORDERS_START);
  } catch (error) {
    // noop
  }
};

export const fetchOrdersPerformanceEnd = ({
  orderCount,
  version,
}: {
  orderCount: number;
  version: 'ORDERS_V1' | 'ORDERS_V2';
}) => {
  try {
    if (!isMeasuringPerformance()) return;

    const measure = performance.measure(ORDERS_FETCH, ORDERS_FETCH_START);
    const duration = measure.duration;
    performance.clearMarks(ORDERS_FETCH_START);
    newRelic.addPageAction('PERFORMANCE_ORDERS_FETCH', {
      duration,
      durationPerOrder: calculateDurationPerOrder(duration, orderCount),
      orderCount,
      ordersExperience: 'new',
      ordersVersion: version,
    });
  } catch (error) {
    // noop
  }
};

export const pollPerformanceStart = () => {
  try {
    if (!isMeasuringPerformance()) return;

    performance.mark(BATCH_ENTRY_POLL_START);
    performance.mark(SHIPMENT_POLL_START);
  } catch (error) {
    // noop
  }
};

export const batchEntryPollPerformanceEnd = ({ orderCount }: { orderCount: number }) => {
  try {
    if (!isMeasuringPerformance()) return;

    const measure = performance.measure(BATCH_ENTRY_POLL, BATCH_ENTRY_POLL_START);
    const duration = measure.duration;
    performance.clearMarks(BATCH_ENTRY_POLL_START);
    newRelic.addPageAction('PERFORMANCE_BATCH_ENTRY_POLL', {
      duration,
      durationPerOrder: calculateDurationPerOrder(duration, orderCount),
      orderCount,
      ordersExperience: 'new',
    });
  } catch (error) {
    // noop
  }
};

export const shipmentPollPerformanceEnd = ({ orderCount }: { orderCount: number }) => {
  try {
    if (!isMeasuringPerformance()) return;

    const measure = performance.measure(SHIPMENT_POLL, SHIPMENT_POLL_START);
    const duration = measure.duration;
    performance.clearMarks(SHIPMENT_POLL_START);
    newRelic.addPageAction('PERFORMANCE_SHIPMENT_POLL', {
      duration,
      durationPerOrder: calculateDurationPerOrder(duration, orderCount),
      orderCount,
      ordersExperience: 'new',
    });
  } catch (error) {
    // noop
  }
};

export const purchaseableOrdersPerformanceEnd = ({
  orderCount,
  version,
}: {
  orderCount: number;
  version: 'ORDERS_V1' | 'ORDERS_V2';
}) => {
  try {
    if (!isMeasuringPerformance()) return;

    const measure = performance.measure(PURCHASEABLE_ORDERS, PURCHASEABLE_ORDERS_START);
    const duration = measure.duration;
    /**
     * This `clearMarks` is wrapped in a `setTimeout` to ensure `PERFORMANCE_ORDERS_FETCH` has
     * an opportunity to be triggered even if the result is empty.
     */
    setTimeout(() => performance.clearMarks(PURCHASEABLE_ORDERS_START));
    newRelic.addPageAction('PERFORMANCE_PURCHASEABLE_ORDERS', {
      duration,
      durationPerOrder: calculateDurationPerOrder(duration, orderCount),
      orderCount,
      ordersExperience: 'new',
      ordersVersion: version,
    });
  } catch (error) {
    // noop
  }
};
